/**
 * Dashboard Analytics Hooks
 * Fetches and manages dashboard analytics data for different user roles
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { useCourseTime } from '../contexts/CourseTimeContext';

export function useDashboardAnalytics(courseFilter = null, options = {}) {
  const { showAllTime } = useCourseTime();
  return useQuery({
    queryKey: [...queryKeys.dashboard.analytics(courseFilter), showAllTime ? 'all' : 'upcoming'],
    queryFn: async () => {
      const params = {};
      if (courseFilter) params.course_uuid = courseFilter;
      if (showAllTime) params.include_past = 'true';
      const response = await apiClient.get('/api/students/dashboard/analytics/', { params });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useHouseOwnerAnalytics() {
  const { showAllTime } = useCourseTime();
  return useQuery({
    queryKey: ['dashboard', 'house-owner', showAllTime ? 'all' : 'upcoming'],
    queryFn: async () => {
      const params = showAllTime ? { include_past: 'true' } : {};
      const response = await apiClient.get('/api/students/dashboard/house-owner/', { params });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Interviewer Dashboard Analytics Hook
 * Fetches interview-focused stats for interviewer users
 */
export function useInterviewerAnalytics() {
  return useQuery({
    queryKey: ['dashboard', 'interviewer'],
    queryFn: async () => {
      const response = await apiClient.get('/api/students/dashboard/interviewer/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });
}
